import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { Loader2, AlertTriangle } from 'lucide-react'

interface MergeDraftDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    projectId: string
    draftId: string
    draftName: string
    targetDraftId: string
    targetDraftName: string
    onSuccess?: () => void
}

export function MergeDraftDialog({
    open,
    onOpenChange,
    projectId,
    draftId,
    draftName,
    targetDraftId,
    targetDraftName,
    onSuccess
}: MergeDraftDialogProps) {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: () =>
            api.post(`/projects/${projectId}/drafts/${draftId}/merge`, { targetDraftId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['drafts', projectId] })
            toast({ title: 'Draft merged', description: 'Draft has been successfully merged.' })
            onOpenChange(false)
            onSuccess?.()
        },
        onError: (error: any) => {
            toast({
                title: 'Merge failed',
                description: error.message || 'Something went wrong',
                variant: 'destructive'
            })
        }
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Merge Draft</DialogTitle>
                    <DialogDescription>
                        Merge "{draftName}" into "{targetDraftName}".
                    </DialogDescription>
                </DialogHeader>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-3 flex items-start gap-3 text-amber-500 text-sm">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium">Warning: This action is destructive.</p>
                        <p className="mt-1 text-amber-500/80">
                            The current contents of "{targetDraftName}" will be archived and replaced by the contents of "{draftName}".
                            "{draftName}" will then be marked as merged.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="default" // Destructive styling? Or just default
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Confirm Merge
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
